/**
 * Event Processor
 *
 * Unified event processing system that orchestrates:
 * - Building standardized event payloads
 * - Applying filters at multiple stages
 * - Adapting data for each active pixel
 * - Dispatching to browser pixels and S2S endpoints
 *
 * @since 1.51.0
 */

(function (pmw, $, undefined) {

	/**
	 * Get the current consent context
	 *
	 * Provides consent status in a structured format for event payloads.
	 * This allows third-party integrations to make decisions based on
	 * the current consent state.
	 *
	 * @returns {object} Consent context object
	 */
	pmw.getConsentContext = function () {
		const categories = pmw.consent?.categories?.get() || {
			statistics : true,
			marketing  : true,
			preferences: true,
			necessary  : true,
		};

		return {
			// Category-level consent status
			categories: {
				statistics : categories.statistics,
				marketing  : categories.marketing,
				preferences: categories.preferences,
				necessary  : categories.necessary,
			},

			// Whether the visitor has actively made a consent choice
			visitor_has_chosen: pmw.consent?.settings?.get()?.visitorHasChosen ?? false,

			// Whether explicit consent mode is active
			explicit_mode: pmw.consent?.explicitConsentModeActive?.() ?? false,
		};
	};

	/**
	 * Global event context that's added to all events
	 *
	 * @returns {object} Context object with user, session, page, and consent data
	 */
	pmw.getEventContext = function () {
		return {
			timestamp : Math.floor(Date.now() / 1000),
			url       : window.location.href,
			referrer  : pmw.retrieveData("referrer") ? pmw.makeFullUrl(pmw.retrieveData("referrer")) : document.referrer,
			user_agent: navigator.userAgent,
			page_type : pmwDataLayer?.shop?.page_type,
			user_id   : pmwDataLayer?.user?.id?.raw || null,

			// Consent status - for third-party integrations and internal use
			consent: pmw.getConsentContext(),
		};
	};

	/**
	 * Process an event through the unified pipeline
	 *
	 * This is the main entry point for processing events with the new filter system.
	 *
	 * @param {string} eventName - Standardized event name (e.g., 'add_to_cart', 'view_item')
	 * @param {object} eventData - Core event data (product, cart, order, etc.)
	 * @returns {Promise<object>} The processed payload
	 */
	pmw.processEvent = async function (eventName, eventData) {

		pmw.console.log(`Processing event: ${eventName}`, eventData);

		try {
			// 1. Create base payload with standardized structure
			let payload = {
				event     : eventName,
				event_data: eventData,
				pixels    : {},
				context   : pmw.getEventContext(),
			};

			// 2. Apply pre-processing filter
			// Use case: Add global tracking params, modify core data before pixel adaptations
			payload = pmw.hooks.applyFilters("pmw_event_payload_pre", payload, eventName);

			// 3. Transform for each active pixel
			const activePixels = pmw.pixelRegistry.getActivePixels();

			for (const pixelName of activePixels) {
				const adapter = pmw.pixelRegistry.getAdapter(pixelName);

				if (!adapter || typeof adapter.adapt !== "function") {
					pmw.console.log(`No adapter found for pixel: ${pixelName}`);
					continue;
				}

			try {
				// Adapt event data to pixel-specific format (supports async adapters)
				let pixelData = await adapter.adapt(payload.event_data, eventName, payload.context);

				// Adapters return null for events they don't support
				// This allows pixels to opt-out of specific events gracefully
				// (e.g., TikTok doesn't support page_view, Pinterest doesn't support add_to_wishlist)
				if (pixelData === null) {
					continue;
				}

				// Apply pixel-specific filter
				// Use case: Modify Facebook-specific data, add custom parameters per pixel
				pixelData = pmw.hooks.applyFilters(
					`pmw_pixel_data_${pixelName}`,
					pixelData,
					eventName,
					payload.event_data,
					payload.context,
				);

				payload.pixels[pixelName] = pixelData;
			} catch (e) {
				pmw.console.error(`Error adapting data for ${pixelName}:`, e);
			}
			}

			// 4. Apply event-specific filter
			// Use case: Modify all pixels for a specific event type (e.g., all add_to_cart events)
			payload = pmw.hooks.applyFilters(
				`pmw_event_payload_${eventName}`,
				payload,
				payload.event_data,
			);

			// 5. Apply post-processing filter
			// Use case: Final validation, logging, debugging, cross-pixel modifications
			payload = pmw.hooks.applyFilters("pmw_event_payload_post", payload, eventName);

			// 6. Add firing decisions to payload for each pixel
			payload.firing = {};
			for (const pixelName of Object.keys(payload.pixels)) {
				payload.firing[pixelName] = pmw.pixelRegistry.getFiringDecision(pixelName, eventName);
			}

			// 7. Dispatch to pixels
			await pmw.dispatchToPixels(payload);

			return payload;

		} catch (e) {
			pmw.console.error(`Error processing event ${eventName}:`, e);
			throw e;
		}
	};

	/**
	 * Dispatch the processed payload to pixels
	 *
	 * Unified dispatch system that:
	 * 1. Dispatches a single pmw:{event} event with firing decisions per pixel
	 * 2. Sends to server for pixels that need S2S
	 *
	 * @param {object} payload - The fully processed event payload with firing decisions
	 * @returns {Promise<void>}
	 */
	pmw.dispatchToPixels = async function (payload) {

		const eventName = payload.event;

		// Dispatch unified event (replaces separate browser/s2s events)
		// Pixels check payload.firing[pixelName].fire_browser to decide if they should fire
		pmw.dispatchUnifiedEvent(payload);

		// Dispatch PUBLIC event for third-party integrations
		// This is the official public API - pmw:event:* events
		// Third parties should listen to these events, not the internal pmw:* events
		pmw.dispatchPublicEvent(payload);

		// Purchase events are handled entirely server-side and should never be sent from browser to server
		// to prevent duplicates and race conditions. However, browser-side pixels still need to fire.
		if (eventName === "purchase") {
			pmw.console.log("Purchase event - skipping browser-to-server transmission (handled server-side only)");
			return; // Return early, but browser pixels have already been dispatched above
		}

		// Page view S2S is handled separately in the pmw:page-view event listener
		// which respects the pageview_event_s2s.is_active setting
		if (eventName === "page_view") {
			return; // Return early, S2S handled in event_listeners.js with proper settings check
		}

		// Only send to server if S2S is globally enabled
		if (pmwDataLayer?.general?.server_2_server?.active) {
			// Extract S2S data for server transmission
			const s2sData = pmw.extractS2SData(payload);

			// Send to server if there's S2S data to send
			if (Object.keys(s2sData).length > 0) {
				const serverPixels = pmw.pixelRegistry.getPixelsForServerFiring(eventName);
				pmw.console.log(`S2S enabled pixels for ${eventName}:`, serverPixels);
				await pmw.sendEventPayloadToServer(s2sData);
			}
		}
	};

	/**
	 * Dispatch unified event to all pixels
	 *
	 * This is the new unified event system that replaces the separate pmw:browser:* and pmw:s2s:* events.
	 * All pixels now listen to pmw:pixel:{event-name} and check payload.firing[pixelName].fire_browser
	 * to determine if they should fire their browser pixel.
	 *
	 * NOTE: This is an INTERNAL event. Third parties should use pmw:event:* events instead.
	 *
	 * Event namespace:
	 * - pmw:{event}        - Entry point events (triggers processEvent)
	 * - pmw:pixel:{event}  - Pixel dispatch events (pixels listen to these)
	 * - pmw:event:{event}  - Public API events (third-party integrations)
	 *
	 * @param {object} payload - The fully processed event payload with firing decisions
	 */
	pmw.dispatchUnifiedEvent = function (payload) {

		const activePixels = pmw.pixelRegistry.getActivePixels();

		if (activePixels.length === 0) {
			return;
		}

		// Convert underscores to hyphens for event name
		const eventName = payload.event.replace(/_/g, "-");

		pmw.console.log(`Dispatching unified event pmw:pixel:${eventName}`, {
			activePixels : activePixels,
			firingStatus : payload.firing,
		});

		// Dispatch single unified event to pixel namespace
		// Pixels check payload.firing[pixelName].fire_browser to decide whether to fire
		jQuery(document).trigger(`pmw:pixel:${eventName}`, payload);
	};

	/**
	 * @deprecated Use pmw.dispatchUnifiedEvent() instead
	 * Dispatch to browser-only pixels (internal) - LEGACY SUPPORT
	 *
	 * NOTE: This function is deprecated. Migrate to listening to pmw:* events
	 * and checking payload.firing[pixelName].fire_browser
	 *
	 * @param {object} payload - The fully processed event payload
	 */
	pmw.dispatchToBrowserPixels = function (payload) {

		const browserPixels = pmw.pixelRegistry.getActiveBrowserOnlyPixels();

		if (browserPixels.length === 0) {
			return;
		}

		pmw.console.log(`[DEPRECATED] Dispatching to browser-only pixels for ${payload.event}:`, browserPixels);

		// Trigger browser-specific event that browser-only pixels can listen to (internal)
		// This allows them to access the filtered, adapted data
		// Convert underscores to hyphens for event name
		const browserEventName = payload.event.replace(/_/g, "-");
		jQuery(document).trigger(`pmw:browser:${browserEventName}`, payload);
	};

	/**
	 * @deprecated Use pmw.dispatchUnifiedEvent() instead
	 * Dispatch to S2S-capable pixels (internal) - LEGACY SUPPORT
	 *
	 * NOTE: This function is deprecated. Migrate to listening to pmw:* events
	 * and checking payload.firing[pixelName].fire_browser
	 *
	 * @param {object} payload - The fully processed event payload
	 */
	pmw.dispatchToS2SPixels = function (payload) {

		const s2sPixels = pmw.pixelRegistry.getActiveS2SPixels();

		if (s2sPixels.length === 0) {
			return;
		}

		pmw.console.log(`[DEPRECATED] Dispatching to S2S-capable pixels for ${payload.event}:`, s2sPixels);

		// Trigger S2S event that S2S-capable pixels listen to (internal)
		// This provides adapted data with event_id for deduplication between browser and server
		// Convert underscores to hyphens for event name
		const s2sEventName = payload.event.replace(/_/g, "-");
		jQuery(document).trigger(`pmw:s2s:${s2sEventName}`, payload);
	};

	/**
	 * Dispatch public event for third-party integrations
	 *
	 * This is the official PUBLIC API for third-party integrations.
	 * Third parties should listen to pmw:event:* events to receive
	 * fully processed payloads with all data including consent status.
	 *
	 * Available events:
	 * - pmw:event:add-to-cart
	 * - pmw:event:remove-from-cart
	 * - pmw:event:view-item
	 * - pmw:event:view-item-list
	 * - pmw:event:select-item
	 * - pmw:event:begin-checkout
	 * - pmw:event:add-payment-info
	 * - pmw:event:add-to-wishlist
	 * - pmw:event:purchase
	 * - pmw:event:search
	 * - pmw:event:page-view
	 *
	 * @param {object} payload - The fully processed event payload
	 */
	pmw.dispatchPublicEvent = function (payload) {

		// Convert underscores to hyphens for event name (e.g., add_to_cart -> add-to-cart)
		const publicEventName = payload.event.replace(/_/g, "-");

		// Dispatch the public event that third parties can listen to
		// This includes all processed data: event_data, pixels, firing, and context (with consent)
		jQuery(document).trigger(`pmw:event:${publicEventName}`, payload);

		pmw.console.log(`Public event dispatched: pmw:event:${publicEventName}`, payload);
	};
	/**
	 * Extract server-to-server data from the payload
	 *
	 * Only pixels with fire_server=true in their firing decision should be included.
	 * This creates a single payload with data for all server-firing pixels.
	 *
	 * @param {object} payload - The full event payload with firing decisions
	 * @returns {object} S2S-only data for server transmission
	 */
	pmw.extractS2SData = function (payload) {

		const s2sData = {
			event: payload.event,
		};

		// Use the new firing decision system
		const serverPixels = pmw.pixelRegistry.getPixelsForServerFiring(payload.event);

		for (const pixelName of serverPixels) {
			if (payload.pixels[pixelName]) {
				s2sData[pixelName] = payload.pixels[pixelName];
			}
		}

		// Add product data if present (for server-side processing)
		if (payload.event_data?.product) {
			s2sData.product = payload.event_data.product;
		}

		return s2sData;
	};

	/**
	 * Check if a pixel should fire its browser pixel for a given payload
	 *
	 * This is a convenience method for pixels to use in their event listeners.
	 * It checks the payload.firing[pixelName].fire_browser flag.
	 *
	 * @param {object} payload - The event payload with firing decisions
	 * @param {string} pixelName - The pixel name to check
	 * @returns {boolean} True if the browser pixel should fire
	 *
	 * @example
	 * jQuery(document).on("pmw:add-to-cart", (event, payload) => {
	 *   if (!pmw.shouldFireBrowserPixel(payload, 'facebook')) return;
	 *   // Fire the pixel...
	 * });
	 */
	pmw.shouldFireBrowserPixel = function (payload, pixelName) {
		return payload.firing?.[pixelName]?.fire_browser === true;
	};

	/**
	 * Check if a pixel should fire server-side for a given payload
	 *
	 * @param {object} payload - The event payload with firing decisions
	 * @param {string} pixelName - The pixel name to check
	 * @returns {boolean} True if the server-side event should fire
	 */
	pmw.shouldFireServerPixel = function (payload, pixelName) {
		return payload.firing?.[pixelName]?.fire_server === true;
	};

	/**
	 * Initialize adapters namespace
	 */
	if (!pmw.adapters) {
		pmw.adapters = {};
	}

	/**
	 * Helper function to register an adapter with optional capabilities
	 *
	 * Firing Modes:
	 * - browser_only: Fire only in browser, no server-side (default for pixels without S2S)
	 * - server_only: Fire only server-side, suppress browser pixel completely
	 * - both: Fire browser AND server with event_id deduplication (Facebook, TikTok default)
	 * - browser_or_server: Fire one OR the other, never both (GA4 purchase)
	 *
	 * @param {string} pixelName - The pixel name
	 * @param {object} adapter - The adapter object with an adapt() method
	 * @param {object} [options] - Optional capabilities configuration
	 * @param {string} [options.mode='browser_only'] - Firing mode: browser_only, server_only, both, browser_or_server
	 * @param {boolean} [options.supportsS2S=false] - Whether pixel supports server-to-server tracking
	 * @param {boolean} [options.supportsEventDeduplication=false] - Whether pixel supports event deduplication
	 * @param {function} [options.isActive] - Function that returns true if pixel is active/loaded
	 *
	 * @example
	 * // Browser-only pixel (Microsoft Ads)
	 * pmw.registerAdapter('bing', BingAdapter, {
	 *   mode: 'browser_only',
	 *   isActive: () => !!pmwDataLayer?.pixels?.bing?.loaded
	 * });
	 *
	 * @example
	 * // S2S pixel with deduplication (Facebook)
	 * pmw.registerAdapter('facebook', FacebookAdapter, {
	 *   mode: 'both',
	 *   supportsS2S: true,
	 *   supportsEventDeduplication: true,
	 *   isActive: () => !!pmwDataLayer?.pixels?.facebook?.pixel_id
	 * });
	 */
	pmw.registerAdapter = function (pixelName, adapter, options = {}) {
		if (!adapter || typeof adapter.adapt !== "function") {
			pmw.console.error(`Invalid adapter for ${pixelName}: must have an adapt() method`);
			return;
		}

		// Store the adapter
		pmw.adapters[pixelName] = adapter;

		// Store pixel capabilities
		if (!pmw.pixelCapabilities) {
			pmw.pixelCapabilities = {};
		}

		// Determine default mode based on S2S support
		let defaultMode = "browser_only";
		if (options.supportsS2S) {
			defaultMode = "both";
		}

		pmw.pixelCapabilities[pixelName] = {
			mode                      : options.mode || defaultMode,
			supportsS2S               : options.supportsS2S || false,
			supportsEventDeduplication: options.supportsEventDeduplication || false,
			isActive                  : options.isActive || null,
		};

		// pmw.console.log(`Adapter registered for: ${pixelName}`, pmw.pixelCapabilities[pixelName]);
	};

}(window.pmw = window.pmw || {}, jQuery));